import React from "react";

export default function PuckedPrivacy() {
  const containerStyle = {
    backgroundColor: "#f4f4f5",
    color: "#18181b",
    minHeight: "100vh",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px",
  };

  const headerStyle = {
    width: "100%",
    maxWidth: "800px",
    padding: "20px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const backBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#52525b",
    textDecoration: "none",
    fontSize: "0.95rem",
    fontWeight: "500",
  };

  const articleStyle = {
    width: "100%",
    maxWidth: "800px",
    backgroundColor: "#ffffff",
    border: "1px solid #e4e4e7",
    borderRadius: "16px",
    padding: "40px",
    margin: "20px 0 60px",
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    boxSizing: "border-box",
  };

  const titleStyle = {
    fontSize: "2.2rem",
    fontWeight: "700",
    margin: "0 0 8px",
    color: "#09090b",
    textAlign: "left",
  };

  const dateStyle = {
    fontSize: "0.9rem",
    color: "#71717a",
    marginBottom: "32px",
    textAlign: "left",
  };

  const headingStyle = {
    fontSize: "1.3rem",
    fontWeight: "600",
    marginTop: "32px",
    marginBottom: "12px",
    color: "#09090b",
    textAlign: "left",
  };

  const paragraphStyle = {
    lineHeight: "1.6",
    color: "#27272a",
    marginBottom: "16px",
    textAlign: "left",
  };

  const listStyle = {
    paddingLeft: "20px",
    marginBottom: "24px",
    lineHeight: "1.6",
    color: "#27272a",
    textAlign: "left",
  };

  const listItemStyle = {
    marginBottom: "8px",
  };

  const linkStyle = {
    color: "#d97706",
    textDecoration: "underline",
    fontWeight: "500",
  };

  const contactBoxStyle = {
    backgroundColor: "#fafafa",
    border: "1px solid #e4e4e7",
    borderRadius: "8px",
    padding: "16px 20px",
    marginTop: "16px",
    textAlign: "left",
  };

  const footerStyle = {
    padding: "24px 0",
    color: "#71717a",
    fontSize: "0.85rem",
    marginTop: "auto",
  };

  return (
    <div style={containerStyle}>
      {/* HEADER */}
      <header style={headerStyle}>
        <a href="/pucked" title="Back to Pucked" style={backBtnStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          <span>Pucked</span>
        </a>

        <div>
          <span style={{ fontWeight: "600", fontSize: "1.1rem" }}>
            Privacy Policy
          </span>
        </div>

        <div style={{ width: "70px" }} />
      </header>

      {/* LEGAL CONTENT */}
      <main
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        <article style={articleStyle}>
          <h1 style={titleStyle}>Privacy Policy</h1>
          <p style={dateStyle}>Last updated: July 25, 2026</p>

          <p style={paragraphStyle}>
            Tilbo Apps ("we", "us", or "our"), based in Belgium, operates the{" "}
            <strong>Pucked</strong> mobile application (the "App"). This Privacy
            Policy explains how we collect, use, and protect your information
            when you use our App.
          </p>

          <h2 style={headingStyle}>1. Information We Collect</h2>
          <p style={paragraphStyle}>
            We collect information that you directly provide to us and data
            generated through your usage of the App:
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>Account Information:</strong> When you register or sign in
              using Firebase Authentication (such as Sign in with Apple or
              email/password), we collect your unique user ID and authentication
              tokens to keep you logged in.
            </li>
            <li style={listItemStyle}>
              <strong>App Data & Coffee Logs:</strong> Your extraction
              parameters (e.g., dose, yield, brew time, ratio, grind settings,
              bean origins, and tasting notes) are stored in Google Cloud
              Firestore to sync across your devices.
            </li>
            <li style={listItemStyle}>
              <strong>In-App Purchase Data:</strong> Subscriptions are processed
              via RevenueCat alongside the Apple App Store or Google Play Store.
              We store anonymous purchase history tokens to grant access to pro
              features.
            </li>
          </ul>

          <h2 style={headingStyle}>2. How We Use Your Information</h2>
          <p style={paragraphStyle}>
            We use the collected data strictly to operate and improve the App:
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              To manage your account and maintain your coffee extraction
              history.
            </li>
            <li style={listItemStyle}>
              To deliver subscription benefits via RevenueCat.
            </li>
            <li style={listItemStyle}>
              To power the <strong>AI Barista Feedback</strong> feature. When
              you request AI feedback, only anonymous extraction parameters
              (such as brew ratio and grind settings) are sent to Google Gemini.{" "}
              <strong>
                No personally identifiable information (PII) like your name or
                email is ever sent to the AI service.
              </strong>
            </li>
          </ul>

          <h2 style={headingStyle}>3. Third-Party Service Providers</h2>
          <p style={paragraphStyle}>
            We rely on trusted third-party cloud infrastructure to run Pucked
            safely and reliably. These services process data solely on our
            behalf:
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>Google Firebase (Authentication & Firestore):</strong> For
              secure user authentication and database storage.
            </li>
            <li style={listItemStyle}>
              <strong>RevenueCat & App Stores:</strong> To process in-app
              subscriptions and manage user entitlements.
            </li>
            <li style={listItemStyle}>
              <strong>Google Gemini API:</strong> To analyze non-personal coffee
              extraction logs and return barista recommendations.
            </li>
          </ul>

          <h2 style={headingStyle}>4. Analytics & Tracking</h2>
          <p style={paragraphStyle}>
            We value your privacy. Pucked does <strong>not</strong> include
            third-party advertising, third-party analytics software (such as
            Google Analytics or Facebook Pixel), or user-tracking tools.
          </p>

          <h2 style={headingStyle}>5. Data Retention & Your Rights (GDPR)</h2>
          <p style={paragraphStyle}>
            As a user in Belgium, the EU, or worldwide, you have rights
            regarding your personal data under the General Data Protection
            Regulation (GDPR):
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>Access & Export:</strong> You can access your coffee logs
              directly inside the App at any time.
            </li>
            <li style={listItemStyle}>
              <strong>Account Deletion:</strong> You have the right to request
              full deletion of your account and associated Firestore data.
            </li>
          </ul>
          <p style={paragraphStyle}>
            To request account deletion or data removal, please contact us at{" "}
            <a href="mailto:tilboapps@gmail.com" style={linkStyle}>
              tilboapps@gmail.com
            </a>
            .
          </p>

          <h2 style={headingStyle}>6. Security</h2>
          <p style={paragraphStyle}>
            We take reasonable precautions to protect your personal information
            using secure HTTPS communication and cloud security rules provided
            by Google Cloud Firebase. However, no method of transmission over
            the internet is 100% secure.
          </p>

          <h2 style={headingStyle}>7. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have any questions or concerns about this Privacy Policy,
            please reach out to us at:
          </p>
          <div style={contactBoxStyle}>
            <p style={{ margin: "2px 0", fontWeight: "600" }}>Tilbo Apps</p>
            <p style={{ margin: "2px 0", color: "#52525b" }}>
              Location: Belgium
            </p>
            <p style={{ margin: "2px 0" }}>
              Email:{" "}
              <a href="mailto:tilboapps@gmail.com" style={linkStyle}>
                tilboapps@gmail.com
              </a>
            </p>
          </div>
        </article>
      </main>

      {/* FOOTER */}
      <footer style={footerStyle}>
        <p style={{ margin: 0 }}>
          © {new Date().getFullYear()} Tilbo Apps. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
